// src/pages/personal/PersonalLayout.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useResponsive } from '@/hooks/useResponsive';

const PersonalLayout: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const mainContentRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  
  const { isMobile, isTablet, isLandscape } = useResponsive();
  
  // Автоматически скрываем сайдбар на мобильных устройствах
  useEffect(() => {
    if (isMobile || isTablet) {
      setIsSidebarOpen(false);
    } else {
      setIsSidebarOpen(true);
    }
  }, [isMobile, isTablet]);
  
  // Закрываем сайдбар на мобильных при изменении маршрута
  useEffect(() => {
    if (isMobile || isTablet) {
      setIsSidebarOpen(false);
    }
  }, [location.pathname, isMobile, isTablet]);
  
  // Функция для переключения состояния сайдбара
  
  // Эффект для анимации появления контента при смене страницы
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    if (mainContentRef.current) {
      mainContentRef.current.style.opacity = '0';
      mainContentRef.current.style.transform = 'translateY(10px)';
      
      const timer = setTimeout(() => {
        if (mainContentRef.current) {
          mainContentRef.current.style.opacity = '1';
          mainContentRef.current.style.transform = 'translateY(0)';
        }
      }, 50);
      
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);
  
  // Вычисляем значения для grid-template-columns с учетом адаптивности
  const getGridTemplateColumns = () => {
    if (isMobile) {
      // Для мобильных полностью скрываем сайдбар
      return 'grid-cols-[0px_1fr]';
    }
    
    if (isTablet && isLandscape) {
      // Для планшетов в горизонтальной ориентации уменьшаем ширину сайдбара
      return isSidebarOpen ? 'grid-cols-[240px_1fr]' : 'grid-cols-[0px_1fr]';
    }
    
    // Стандартный размер сайдбара
    return isSidebarOpen ? 'grid-cols-[280px_1fr]' : 'grid-cols-[0px_1fr]';
  };
  
  // Определяем, нужно ли показывать мобильную навигацию
  const shouldShowMobileNav = isMobile || isTablet;

  // Определяем, находимся ли мы на странице профиля или настроек пользователя

  // Функции обработки событий для мобильного хедера

  // Определяем максимальную высоту контента с учетом мобильной навигации
  const getContentStyles = () => {
    const styles: React.CSSProperties = {
      minHeight: '500px',
      transition: 'all 0.3s ease'
    };
    
    if (shouldShowMobileNav) {
      // Добавляем отступ снизу для нижней навигации
      styles.paddingBottom = isMobile ? '70px' : '80px';
    }
    
    return styles;
  };
  
  // Определяем отступы в зависимости от устройства
  const getContentPadding = () => {
    if (isMobile) return 'p-2';
    if (isTablet) return 'p-4';
    return 'p-3 sm:p-6';
  };
  
  return (
    <div className="min-h-screen bg-[#EFF6EF] flex flex-col relative" ref={contentRef}>      
      {/* Main container with adaptive grid */}
      <div className={`
        grid 
        ${getGridTemplateColumns()}
        transition-all 
        duration-300 
        gap-2 sm:gap-4
        max-w-7xl 
        mx-auto 
        w-full
        ${isMobile ? 'px-1' : 'px-2 sm:px-4 md:px-6 lg:px-8'}
        ${isMobile ? 'py-2' : 'py-4 sm:py-6'} 
        flex-1
      `}>
        {/* Правая колонка с основным контентом - без левой колонки с боковым меню */}
        <div className="col-span-2 overflow-hidden transition-all duration-300 w-full">
          <div 
            ref={mainContentRef}
            className={`w-full overflow-x-auto bg-white rounded-lg shadow-sm ${getContentPadding()} main-content`}
            style={getContentStyles()}
          >
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalLayout;